/**
 * ModalManager - Generic modal overlay creation and management
 *
 * Provides consistent modal creation patterns used throughout GameUI.
 */

export class ModalManager {
    constructor() {
        this.activeModals = [];
        this.modalQueue = [];
        this.isProcessingQueue = false;
    }

    /**
     * Queue a modal to be shown after current modals close
     * Useful for startup sequences where multiple modals need to appear in order
     * @param {function} modalCreator - Function that creates and shows the modal, returns { close } or Promise
     */
    queue(modalCreator) {
        this.modalQueue.push(modalCreator);
        this.processQueue();
    }

    /**
     * Process the modal queue - show next modal when current one closes
     */
    processQueue() {
        // If already processing or queue is empty, do nothing
        if (this.isProcessingQueue || this.modalQueue.length === 0) {
            return;
        }

        // If there are active modals, wait for them to close
        if (this.activeModals.length > 0) {
            return;
        }

        this.isProcessingQueue = true;
        const nextModal = this.modalQueue.shift();

        // Create the modal and wait for it to close
        const result = nextModal();

        // If the modal creator returns a close function, we track it
        // When modal closes, it will call processQueue again via onClose
        this.isProcessingQueue = false;
    }

    /**
     * Called when a modal closes - process next in queue
     */
    onModalClosed() {
        // Small delay to allow DOM cleanup
        setTimeout(() => this.processQueue(), 50);
    }

    /**
     * Create a modal overlay with container
     * @param {object} options - Modal configuration
     * @param {string} [options.className] - Additional class name for the modal
     * @param {boolean} [options.closeOnBackdrop=true] - Whether clicking backdrop closes modal
     * @param {boolean} [options.blur=true] - Apply backdrop blur
     * @param {function} [options.onClose] - Callback when modal closes
     * @param {string} [options.maxWidth='800px'] - Max width of modal container
     * @param {string} [options.maxHeight='90vh'] - Max height of modal container
     * @param {string} [options.padding='30px'] - Padding of modal container
     * @returns {{overlay: HTMLElement, container: HTMLElement, close: function}} Modal elements and close function
     */
    create(options = {}) {
        const {
            className = '',
            closeOnBackdrop = true,
            blur = true,
            onClose = null,
            maxWidth = '800px',
            maxHeight = '90vh',
            padding = '30px'
        } = options;

        // Create overlay
        const overlay = document.createElement('div');
        overlay.className = `gameui-modal-overlay ${className}`.trim();
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.85);
            ${blur ? 'backdrop-filter: blur(8px);' : ''}
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 10000;
            pointer-events: auto;
        `;

        // Create container
        const container = document.createElement('div');
        container.className = 'gameui-modal-container';
        container.style.cssText = `
            background: rgba(20, 20, 35, 0.98);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 12px;
            padding: ${padding};
            max-width: ${maxWidth};
            max-height: ${maxHeight};
            overflow-y: auto;
            box-shadow: 0 10px 50px rgba(0, 0, 0, 0.5);
            font-family: 'Courier New', monospace;
        `;

        // Close function
        const close = () => {
            if (onClose) {
                onClose();
            }
            overlay.remove();
            const idx = this.activeModals.indexOf(overlay);
            if (idx > -1) {
                this.activeModals.splice(idx, 1);
            }
            // Process next modal in queue
            this.onModalClosed();
        };

        // Handle backdrop click
        if (closeOnBackdrop) {
            overlay.addEventListener('click', (e) => {
                if (e.target === overlay) {
                    close();
                }
            });
        }

        overlay.appendChild(container);
        document.body.appendChild(overlay);
        this.activeModals.push(overlay);

        return { overlay, container, close };
    }

    /**
     * Create a modal with header (title + close button)
     * @param {object} options - Modal configuration
     * @param {string} options.title - Modal title
     * @param {function} [options.onClose] - Close callback
     * @param {...} - Other modal options
     * @returns {{overlay: HTMLElement, container: HTMLElement, content: HTMLElement, close: function}}
     */
    createWithHeader(options = {}) {
        const { title = '', onClose = null, ...rest } = options;

        const modal = this.create({ ...rest, onClose });

        // Create header
        const header = document.createElement('div');
        header.className = 'gameui-modal-header';
        header.style.cssText = `
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        `;

        // Title
        const titleElement = document.createElement('div');
        titleElement.className = 'gameui-modal-title';
        titleElement.textContent = title;
        titleElement.style.cssText = `
            font-size: 24px;
            font-weight: bold;
            color: rgba(255, 255, 255, 0.9);
            letter-spacing: 2px;
        `;
        header.appendChild(titleElement);

        // Close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'gameui-modal-close';
        closeBtn.textContent = '\u00D7';
        closeBtn.style.cssText = `
            font-size: 28px;
            color: rgba(255, 255, 255, 0.6);
            cursor: pointer;
            background: none;
            border: none;
            padding: 0;
            line-height: 1;
            transition: color 0.15s ease;
        `;
        closeBtn.addEventListener('mouseover', () => {
            closeBtn.style.color = 'rgba(255, 255, 255, 0.9)';
        });
        closeBtn.addEventListener('mouseout', () => {
            closeBtn.style.color = 'rgba(255, 255, 255, 0.6)';
        });
        closeBtn.addEventListener('click', modal.close);
        header.appendChild(closeBtn);

        modal.container.appendChild(header);

        // Content area
        const content = document.createElement('div');
        content.className = 'gameui-modal-content';
        modal.container.appendChild(content);

        return {
            ...modal,
            header,
            content
        };
    }

    /**
     * Create a Cassette Future styled confirmation modal
     * @param {object} options - Confirmation options
     * @param {string} options.title - Modal title
     * @param {string|HTMLElement} options.message - Message content
     * @param {string} [options.confirmText='Confirm'] - Confirm button text
     * @param {string} [options.cancelText='Cancel'] - Cancel button text
     * @param {string} [options.confirmStyle='primary'] - Confirm button style: 'primary', 'danger', or 'warning'
     * @param {function} [options.onConfirm] - Confirm callback
     * @param {function} [options.onCancel] - Cancel callback
     * @returns {object} Modal object with overlay, modal, body, close
     */
    createConfirmation(options = {}) {
        const {
            title = 'Confirm',
            message = '',
            confirmText = 'Confirm',
            cancelText = 'Cancel',
            confirmStyle = 'primary', // 'primary', 'danger', 'warning'
            onConfirm = null,
            onCancel = null
        } = options;

        // Create CF-styled modal
        const overlay = document.createElement('div');
        overlay.className = 'cf-ui cf-modal-overlay';

        const modal = document.createElement('div');
        modal.className = 'cf-modal';
        modal.style.maxWidth = '400px';

        // Header
        const header = document.createElement('div');
        header.className = 'cf-modal-header';

        const titleEl = document.createElement('h2');
        titleEl.className = 'cf-modal-title';
        titleEl.textContent = title;
        header.appendChild(titleEl);

        const closeBtn = document.createElement('button');
        closeBtn.className = 'cf-modal-close';
        closeBtn.textContent = '\u00D7';
        header.appendChild(closeBtn);

        modal.appendChild(header);

        // Body
        const body = document.createElement('div');
        body.className = 'cf-modal-body';

        // Message
        const messageEl = document.createElement('div');
        messageEl.style.cssText = `
            font-size: var(--cf-font-size-sm);
            color: var(--cf-text-secondary);
            line-height: var(--cf-leading-relaxed);
            margin-bottom: var(--cf-space-lg);
            white-space: pre-line;
        `;
        if (typeof message === 'string') {
            messageEl.textContent = message;
        } else {
            messageEl.appendChild(message);
        }
        body.appendChild(messageEl);

        // Button container
        const buttons = document.createElement('div');
        buttons.className = 'cf-button-group';

        // Cancel button
        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'cf-button';
        cancelBtn.textContent = cancelText;
        buttons.appendChild(cancelBtn);

        // Confirm button
        const confirmBtn = document.createElement('button');
        confirmBtn.className = 'cf-button';
        // Apply style variant
        if (confirmStyle === 'danger') {
            confirmBtn.classList.add('cf-button-danger');
        } else if (confirmStyle === 'warning') {
            // Warning style - amber/orange
            confirmBtn.style.color = 'var(--cf-warning)';
            confirmBtn.style.borderColor = 'rgba(232, 184, 74, 0.5)';
        } else {
            confirmBtn.classList.add('primary');
        }
        confirmBtn.textContent = confirmText;
        buttons.appendChild(confirmBtn);

        body.appendChild(buttons);
        modal.appendChild(body);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        // Close function
        const close = () => {
            overlay.remove();
            const idx = this.activeModals.indexOf(overlay);
            if (idx > -1) {
                this.activeModals.splice(idx, 1);
            }
            this.onModalClosed();
        };

        // Event listeners
        closeBtn.addEventListener('click', () => {
            if (onCancel) onCancel();
            close();
        });
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                if (onCancel) onCancel();
                close();
            }
        });
        cancelBtn.addEventListener('click', () => {
            if (onCancel) onCancel();
            close();
        });
        confirmBtn.addEventListener('click', () => {
            if (onConfirm) onConfirm();
            close();
        });

        // Escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (onCancel) onCancel();
                close();
                document.removeEventListener('keydown', handleEscape);
            }
        };
        document.addEventListener('keydown', handleEscape);

        this.activeModals.push(overlay);

        return { overlay, modal, body, close };
    }

    /**
     * Create a full-screen overlay without a boxed container
     * Used for selection screens and other full-viewport interfaces
     * @param {object} options - Overlay configuration
     * @param {string} [options.className] - Additional class name
     * @param {boolean} [options.closeOnBackdrop=true] - Whether clicking backdrop closes overlay
     * @param {number} [options.zIndex=300] - Z-index for the overlay
     * @param {function} [options.onClose] - Callback when overlay closes
     * @returns {{overlay: HTMLElement, content: HTMLElement, close: function}}
     */
    createOverlay(options = {}) {
        const {
            className = '',
            closeOnBackdrop = true,
            zIndex = 300,
            onClose = null
        } = options;

        // Create overlay
        const overlay = document.createElement('div');
        overlay.className = `gameui-fullscreen-overlay ${className}`.trim();
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.92);
            z-index: ${zIndex};
            pointer-events: auto;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            padding: 12px;
            box-sizing: border-box;
        `;

        // Content wrapper
        const content = document.createElement('div');
        content.className = 'gameui-fullscreen-content';
        content.style.cssText = `
            display: flex;
            flex-direction: column;
            align-items: center;
            max-width: 100%;
            max-height: 100%;
        `;

        // Close function
        const close = () => {
            if (onClose) {
                onClose();
            }
            overlay.remove();
            const idx = this.activeModals.indexOf(overlay);
            if (idx > -1) {
                this.activeModals.splice(idx, 1);
            }
            this.onModalClosed();
        };

        // Handle backdrop click
        if (closeOnBackdrop) {
            overlay.addEventListener('click', (e) => {
                if (e.target === overlay) {
                    close();
                }
            });
        }

        overlay.appendChild(content);
        document.body.appendChild(overlay);
        this.activeModals.push(overlay);

        return { overlay, content, close };
    }

    /**
     * Close all active modals
     */
    closeAll() {
        [...this.activeModals].forEach(modal => {
            modal.remove();
        });
        this.activeModals = [];
    }

    /**
     * Check if any modals are currently open
     * @returns {boolean}
     */
    hasOpenModals() {
        return this.activeModals.length > 0;
    }
}
